import { create } from 'zustand';

/**
 * `await confirmDialog({...})` — the app's replacement for `window.confirm`,
 * and `await promptDialog({...})` for `window.prompt`.
 *
 * The browser's own boxes can't be styled, read as a system warning rather
 * than a question from Flow, and block the whole tab. These ask the same
 * question in the app's ConfirmDialog and answer the same way, so a call site
 * keeps its shape:
 *
 *   if (!window.confirm('Delete this photo permanently?')) return;
 *   if (!(await confirmDialog({ title: 'Delete this photo permanently?', ... }))) return;
 *
 * confirmDialog: `true` on Confirm; `false` on Cancel, Escape or a click
 * outside. promptDialog: what was typed ('' for nothing) on Confirm; `null`
 * otherwise — exactly window.prompt's answers.
 *
 * The dialog is drawn by <ConfirmHost />, mounted once at the root next to the
 * Toaster, so a question raised on a public page or in the researcher portal
 * is shown too.
 */

export interface ConfirmOptions {
  /** The question, e.g. `Delete template "Welcome"?` — name the thing it acts on. */
  title: string;
  /** What happens if they go ahead. */
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything destructive: a red confirm button and a warning icon. */
  variant?: 'default' | 'danger' | 'warning' | 'info';
}

export interface PromptOptions extends ConfirmOptions {
  /** The text field's label, e.g. "Why, briefly? (optional)". */
  inputLabel: string;
  placeholder?: string;
}

export interface PendingConfirm extends ConfirmOptions {
  id: number;
  /** Present when the question also asks for a line of text (promptDialog). */
  input?: { label: string; placeholder?: string };
  /** What had focus when the question was asked — usually the button that
   *  asked it — so the host can hand focus back when it is answered. */
  opener: HTMLElement | null;
}

interface ConfirmQueue {
  /** Oldest first; the host shows the first. */
  pending: PendingConfirm[];
}

interface Answer {
  confirmed: boolean;
  text: string;
}

export const useConfirmQueue = create<ConfirmQueue>(() => ({ pending: [] }));

const resolvers = new Map<number, (answer: Answer) => void>();
let nextId = 0;
let mountedHosts = 0;

function ask(request: Omit<PendingConfirm, 'id' | 'opener'>): Promise<Answer> {
  const id = ++nextId;
  const opener = typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null);
  return new Promise<Answer>((resolve) => {
    resolvers.set(id, resolve);
    useConfirmQueue.setState((state) => ({ pending: [...state.pending, { ...request, id, opener }] }));
  });
}

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  // No host on screen (a component rendered on its own, outside App) would
  // leave the promise unanswered and the button doing nothing. Ask the old way
  // instead.
  if (mountedHosts === 0) {
    return Promise.resolve(window.confirm(`${options.title}\n\n${options.description}`));
  }
  return ask(options).then((answer) => answer.confirmed);
}

export function promptDialog({ inputLabel, placeholder, ...options }: PromptOptions): Promise<string | null> {
  if (mountedHosts === 0) {
    return Promise.resolve(window.prompt(`${options.title}\n\n${options.description}\n\n${inputLabel}`));
  }
  return ask({ ...options, input: { label: inputLabel, placeholder } }).then((answer) =>
    answer.confirmed ? answer.text : null,
  );
}

/** Answer one request and take it off the queue. Called by the host. */
export function settleConfirm(id: number, confirmed: boolean, text = ''): void {
  const resolve = resolvers.get(id);
  resolvers.delete(id);
  useConfirmQueue.setState((state) => ({ pending: state.pending.filter((p) => p.id !== id) }));
  resolve?.({ confirmed, text });
}

/**
 * Answer every open request "no". The host calls this when the page changes:
 * the browser's boxes held the tab still until they were answered, so no
 * question ever outlived the page that asked it, and none should now.
 */
export function cancelAllConfirms(): void {
  for (const { id } of useConfirmQueue.getState().pending) settleConfirm(id, false);
}

/** The host registers itself so the helpers know someone will answer. */
export function registerConfirmHost(): () => void {
  mountedHosts += 1;
  return () => {
    mountedHosts -= 1;
    if (mountedHosts === 0) cancelAllConfirms();
  };
}
